import { cosA, iatan2, idiv, imax, isqrt, sinA, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, HuntTrap } from '../types.ts';
import { damageEnemy, damageHero, ev, isFlying } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The hunt (El Cuerno 3, docs/SAGAS.md): the Old Uro, a bull of ash, can't be killed in a day. Each outing hits it
 * what it can and goes home; its wounds stay for the next, whoever plays it, and so do the traps laid for it (the
 * campaign keeps both, campaign/sides.ts). It never strikes: it charges whatever moves within sight, heroes and
 * nightmares alike, and a hero who stands still is invisible to it. A charge that loses its hero pulls up short,
 * snorting; one that crosses a trap ends in it.
 */

/** (dx, dy) scaled to `px` long (nothing if it has no length). */
const scaled = (dx: number, dy: number, px: number): [number, number] => {
  const d = isqrt(dx * dx + dy * dy);
  return d === 0 ? [0, 0] : [idiv(dx * px, d), idiv(dy * px, d)];
};

const uroOf = (s: BattleState): Enemy | undefined => (s.bossId ? s.enemies.find((e) => e.id === s.bossId && e.kind === 'uro' && e.hp > 0) : undefined);

/** A hero it can see: up, and moved within the last few ticks. */
const moving = (s: BattleState, h: Hero): boolean => {
  const r = s.rules?.hunt;
  if (!r || h.downTicks > 0) return false;
  const seen = s.hunt?.seen.find((x) => x.id === h.id);
  return !seen || seen.still < r.still;
};

/** Where it can see a thing moving, if it still is one it can see (a hero gone still, or anything fallen, is lost). */
const sighted = (s: BattleState, id: number): { x: number; y: number; hero: Hero | null } | null => {
  const h = s.heroes.find((x) => x.id === id);
  if (h) return moving(s, h) ? { x: h.x, y: h.y, hero: h } : null;
  const e = s.enemies.find((x) => x.id === id);
  return e && e.hp > 0 && e.anim === 'walk' ? { x: e.x, y: e.y, hero: null } : null;
};

export const huntTick = (s: BattleState): void => {
  const r = s.rules?.hunt;
  if (!r) return;
  const st = (s.hunt ??= { mode: 'graze', t: 0, tgt: 0, gx: r.home.x, gy: r.home.y, dx: 0, dy: 0, hit: [], seen: [], traps: r.traps.map((x) => ({ ...x })), sprung: [], laid: 0, wounds: [], left: false, by: '' });
  if (!s.started) return;
  // Its whole life shows on its bar; it comes out with what it had left.
  const u = uroOf(s);
  if (u && u.maxHp !== r.max) u.maxHp = r.max;
  // Who stood still how long.
  for (const h of s.heroes) {
    const seen = st.seen.find((x) => x.id === h.id);
    if (!seen) st.seen.push({ id: h.id, x: h.x, y: h.y, still: 0 });
    else if (seen.x === h.x && seen.y === h.y) seen.still++;
    else {
      seen.x = h.x;
      seen.y = h.y;
      seen.still = 0;
    }
  }
};

/** A trap at the hero's feet (up to `per` an outing each, `max` on the field). */
export const huntTrap = (s: BattleState, h: Hero): void => {
  const r = s.rules?.hunt;
  const st = s.hunt;
  if (!r || !st || h.downTicks > 0 || s.result !== 'running') return;
  if (st.traps.length >= r.trap.max || st.traps.filter((x) => !x.old && x.pid === h.pid).length >= r.trap.per) return;
  // Not on top of another one.
  if (st.traps.some((x) => withinRange(x.x, x.y, h.x, h.y, r.trap.r))) return;
  st.laid++;
  st.traps.push({ id: st.laid, x: h.x, y: h.y, pid: h.pid, old: false });
  ev(s, { t: 'hunt', kind: 'trap', x: h.x, y: h.y });
};

/** The outing ends at the camp: every hero still up has to be back by the node. */
export const huntLeave = (s: BattleState): void => {
  const r = s.rules?.hunt;
  const st = s.hunt;
  if (!r || !st || s.result !== 'running') return;
  const up = s.heroes.filter((h) => h.downTicks === 0);
  if (up.length && up.every((h) => withinRange(h.x, h.y, 0, 0, r.camp))) st.left = true;
};

/** What a blow did to it, and whose (the chronicle tells who wounded it, and who finished it). */
export const huntWound = (s: BattleState, pid: string | undefined, dmg: number, dead: boolean): void => {
  const st = s.hunt;
  if (!st || !pid) return;
  const w = st.wounds.find((x) => x.pid === pid);
  if (w) w.dmg += dmg;
  else st.wounds.push({ pid, dmg });
  if (dead) st.by = pid;
};

/** Ran into a trap: caught a while, and hurt (the one who laid it wounded it). */
const caught = (s: BattleState, e: Enemy, tr: HuntTrap): void => {
  const r = s.rules?.hunt;
  const st = s.hunt;
  if (!r || !st) return;
  st.traps = st.traps.filter((x) => x !== tr);
  if (tr.old) st.sprung.push(tr.id);
  st.mode = 'rest';
  st.t = r.charge.rest;
  e.stun = imax(e.stun, r.trap.hold);
  e.anim = 'stun';
  ev(s, { t: 'hunt', kind: 'caught', x: tr.x, y: tr.y });
  damageEnemy(s, e, r.trap.damage, { pid: tr.pid, ignoresArmor: true, noReact: true });
};

/** Moves it a step; false if a trap or the cliff stopped it. */
const stride = (s: BattleState, e: Enemy, dx: number, dy: number): boolean => {
  const r = s.rules?.hunt;
  const st = s.hunt;
  if (!r || !st) return false;
  const nx = e.x + dx;
  const ny = e.y + dy;
  if (!withinRange(nx, ny, 0, 0, r.edge)) return false;
  e.x = nx;
  e.y = ny;
  if (dx !== 0 || dy !== 0) e.facing = iatan2(dy, dx);
  const tr = st.traps.find((x) => withinRange(x.x, x.y, e.x, e.y, r.trap.r));
  if (tr) {
    caught(s, e, tr);
    return false;
  }
  return true;
};

/** The nearest thing moving within sight: a hero not standing still, or a walking nightmare. */
const quarry = (s: BattleState, e: Enemy): number => {
  const r = s.rules?.hunt;
  if (!r) return 0;
  let best = 0;
  let bd = r.sight * r.sight;
  const look = (id: number, x: number, y: number): void => {
    const dx = x - e.x;
    const dy = y - e.y;
    const d = dx * dx + dy * dy;
    if (d < bd) {
      bd = d;
      best = id;
    }
  };
  for (const h of s.heroes) if (moving(s, h)) look(h.id, h.x, h.y);
  for (const o of s.enemies) if (o !== e && o.hp > 0 && o.anim === 'walk' && !isFlying(o) && o.kind !== 'boneWall') look(o.id, o.x, o.y);
  return best;
};

/** The Uro's turn (it never takes a nightmare's usual one). */
export const uroTick = (s: BattleState, e: Enemy): void => {
  const r = s.rules?.hunt;
  const st = s.hunt;
  if (!r || !st) return;
  const c = r.charge;
  if (st.mode === 'graze') {
    e.anim = 'walk';
    if (withinRange(e.x, e.y, st.gx, st.gy, 200)) {
      // Somewhere else to graze, around its place.
      const a = rngInt(s.rng, 1024);
      const d = rngInt(s.rng, r.roam);
      st.gx = r.home.x + idiv(cosA(a) * d, 10000);
      st.gy = r.home.y + idiv(sinA(a) * d, 10000);
    } else {
      stepToward(e, st.gx, st.gy, r.walk);
      const tr = st.traps.find((x) => withinRange(x.x, x.y, e.x, e.y, r.trap.r));
      if (tr) {
        caught(s, e, tr);
        return;
      }
    }
    if (s.tick % 3 !== 0) return;
    const id = quarry(s, e);
    if (!id) return;
    st.mode = 'wind';
    st.t = r.wind;
    st.tgt = id;
    e.anim = 'cast';
    e.animT = 0;
    ev(s, { t: 'hunt', kind: 'wind', x: e.x, y: e.y });
    return;
  }
  if (st.mode === 'wind') {
    const at = sighted(s, st.tgt);
    if (!at) {
      // It went still (or fell): the Uro blows, and looks again.
      st.mode = 'rest';
      st.t = idiv(c.rest, 2);
      e.anim = 'idle';
      ev(s, { t: 'hunt', kind: 'snort', x: e.x, y: e.y });
      return;
    }
    e.facing = iatan2(at.y - e.y, at.x - e.x);
    if (--st.t > 0) return;
    [st.dx, st.dy] = scaled(at.x - e.x, at.y - e.y, c.speed);
    if (st.dx === 0 && st.dy === 0) [st.dx, st.dy] = [c.speed, 0];
    st.mode = 'charge';
    st.t = c.ticks;
    st.hit = [];
    e.anim = 'walk';
    ev(s, { t: 'hunt', kind: 'charge', x: e.x, y: e.y, a: e.facing });
    return;
  }
  if (st.mode === 'charge') {
    // The hero it ran at went still: it pulls up short of it, blows, and goes on its way.
    const h = s.heroes.find((x) => x.id === st.tgt);
    if (h && !moving(s, h) && h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, c.brake)) {
      st.mode = 'rest';
      st.t = c.rest;
      e.anim = 'idle';
      ev(s, { t: 'hunt', kind: 'snort', x: e.x, y: e.y });
      return;
    }
    if (!stride(s, e, st.dx, st.dy) || --st.t <= 0) {
      if (st.mode === 'charge') {
        st.mode = 'rest';
        st.t = c.rest;
        e.anim = 'idle';
      }
      return;
    }
    // Whatever moves in its way goes down (a still hero, it doesn't even see).
    for (const x of s.heroes) {
      if (st.hit.includes(x.id) || !moving(s, x) || !withinRange(x.x, x.y, e.x, e.y, c.hit)) continue;
      st.hit.push(x.id);
      damageHero(s, x, c.hero, e);
      const [sx, sy] = scaled(x.x - e.x + st.dx, x.y - e.y + st.dy, c.shove);
      x.shove = 8;
      x.shoveX = sx;
      x.shoveY = sy;
      ev(s, { t: 'hunt', kind: 'gore', x: x.x, y: x.y });
    }
    for (const o of s.enemies) {
      if (o === e || o.hp <= 0 || isFlying(o) || o.kind === 'boneWall' || st.hit.includes(o.id) || !withinRange(o.x, o.y, e.x, e.y, c.hit)) continue;
      st.hit.push(o.id);
      o.stun = imax(o.stun, 12);
      damageEnemy(s, o, c.foe, { pid: '', ignoresArmor: true, noReact: true });
      ev(s, { t: 'hunt', kind: 'trample', x: o.x, y: o.y });
    }
    return;
  }
  // Blowing after a charge: the time to hit it.
  e.anim = 'idle';
  if (--st.t <= 0) {
    st.mode = 'graze';
    st.gx = e.x;
    st.gy = e.y;
  }
};

/** Won the moment it falls; the outing ends at its time, or back at the camp (its wounds stay either way). */
export const huntResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.hunt;
  if (!r) return null;
  if (s.bossId && !s.enemies.some((e) => e.id === s.bossId && e.hp > 0)) return 'won';
  if (s.hunt?.left || s.waveT >= r.out) return 'lost';
  return null;
};
